import { useEffect, useRef, useState } from 'react';
import {
  browserMicrophoneAdapter,
  isMicrophoneError,
  isSilentRecordingError,
  type MicrophoneAdapter,
  type MicrophoneStream,
  type RecordedAudio,
} from '../data/microphone';
import {
  encodeWavBlob,
  resamplePcm,
  sliceIntoSeconds,
  TARGET_SAMPLE_RATE,
} from '../ml/audioFeatures';
import { LiveSpectrogram } from './LiveSpectrogram';
import { useObjectUrl } from './useObjectUrl';
import './MicPanel.css';

type Status = 'opening' | 'ready' | 'recording' | 'extracting' | 'error';

export interface MicPanelProps {
  /** Total seconds to record in one shot. The kid then extracts 1-second
   *  clips out of the recording — TM-style. */
  recordSeconds: number;
  /** Required clips for this class — used in the prompt copy. */
  minimum: number;
  /** Called for each 1-second clip extracted from a recording. May fire
   *  several times in a row when the kid extracts a long recording. */
  onCapture: (blob: Blob) => void | Promise<void>;
  onClose: () => void;
  /** For tests; defaults to the real browser MediaDevices adapter. */
  microphone?: MicrophoneAdapter;
}

/**
 * Audio capture surface — TM-parity. The kid clicks "Record N seconds",
 * the panel records one continuous block of audio, the kid plays it back
 * and decides whether to keep it. "Extract Sample" slices the recording
 * into 1-second clips and hands each one to `onCapture`. The kid sees
 * how many clips were extracted from the recording before deciding to
 * record again.
 */
export function MicPanel({
  recordSeconds,
  minimum,
  onCapture,
  onClose,
  microphone = browserMicrophoneAdapter,
}: MicPanelProps): JSX.Element {
  const [status, setStatus] = useState<Status>('opening');
  const [error, setError] = useState<string | null>(null);
  const [openAttempt, setOpenAttempt] = useState(0);
  const [pending, setPending] = useState<RecordedAudio | null>(null);
  const [pendingBlob, setPendingBlob] = useState<Blob | null>(null);
  const [recordingProgress, setRecordingProgress] = useState(0);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
  const handleRef = useRef<MicrophoneStream | null>(null);
  const previewUrl = useObjectUrl(pendingBlob);

  // 1. Open the mic on mount; close it on unmount.
  useEffect(() => {
    let cancelled = false;
    setStatus('opening');
    setError(null);

    (async (): Promise<void> => {
      let opened: MicrophoneStream;
      try {
        opened = await microphone.open();
      } catch (err) {
        if (cancelled) return;
        const message = isMicrophoneError(err) ? err.message : 'Could not turn on the microphone.';
        setError(message);
        setStatus('error');
        return;
      }
      if (cancelled) {
        microphone.close(opened);
        return;
      }
      handleRef.current = opened;
      setAnalyser(opened.analyser);
      setAudioContext(opened.context);
      setStatus('ready');
    })();

    return () => {
      cancelled = true;
      setAnalyser(null);
      setAudioContext(null);
      if (handleRef.current) {
        microphone.close(handleRef.current);
        handleRef.current = null;
      }
    };
  }, [microphone, openAttempt]);

  const startRecording = async (): Promise<void> => {
    const handle = handleRef.current;
    if (!handle || status !== 'ready') return;
    setStatus('recording');
    setError(null);
    setPending(null);
    setPendingBlob(null);
    setRecordingProgress(0);

    // Tick a 100ms timer for the on-screen progress; wall-clock-driven so
    // a kid can see the seconds counting down even if the audio thread
    // is busy.
    const start = Date.now();
    const ticker = setInterval(() => {
      const elapsed = (Date.now() - start) / 1000;
      setRecordingProgress(Math.min(1, elapsed / recordSeconds));
    }, 100);
    try {
      const recorded = await microphone.recordSeconds(handle, recordSeconds);
      const downsampled = resamplePcm(recorded.pcm, recorded.sampleRate, TARGET_SAMPLE_RATE);
      const blob = encodeWavBlob(downsampled, TARGET_SAMPLE_RATE);
      setPending({ pcm: downsampled, sampleRate: TARGET_SAMPLE_RATE });
      setPendingBlob(blob);
    } catch (err) {
      const message = isSilentRecordingError(err)
        ? err.message
        : isMicrophoneError(err)
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Could not record that clip.';
      setError(message);
    } finally {
      clearInterval(ticker);
      setRecordingProgress(0);
      setStatus('ready');
    }
  };

  const discardPending = (): void => {
    setPending(null);
    setPendingBlob(null);
    setError(null);
  };

  const extractPending = async (): Promise<void> => {
    if (!pending || status !== 'ready') return;
    setStatus('extracting');
    try {
      const slices = sliceIntoSeconds(pending.pcm, pending.sampleRate, 1);
      for (const slice of slices) {
        const blob = encodeWavBlob(slice, pending.sampleRate);
        await onCapture(blob);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not extract those samples.');
    } finally {
      setPending(null);
      setPendingBlob(null);
      setStatus('ready');
    }
  };

  const handleRetry = (): void => {
    setOpenAttempt((n) => n + 1);
  };

  const sliceCount = pending ? Math.floor(pending.pcm.length / pending.sampleRate) : 0;
  const buttonLabel =
    status === 'recording'
      ? `Listening… (${Math.ceil(recordSeconds * (1 - recordingProgress))}s left)`
      : `Record ${recordSeconds} second${recordSeconds === 1 ? '' : 's'}`;

  return (
    <div className="bl-mic" role="group" aria-label="Microphone capture">
      <div className="bl-mic__indicator">
        <span
          className={`bl-mic__pip${status === 'recording' ? ' bl-mic__pip--live' : ''}`}
          aria-hidden="true"
        />
        {status === 'opening' && (
          <p className="bl-mic__overlay" role="status">
            Turning on the microphone…
          </p>
        )}
        {status === 'error' && (
          <div className="bl-mic__overlay bl-mic__overlay--error" role="alert">
            <p>{error}</p>
            <button type="button" className="bl-mic__retry" onClick={handleRetry}>
              Try again
            </button>
          </div>
        )}
        {status === 'ready' && !pending && (
          <>
            <p className="bl-mic__overlay" role="status">
              {minimum > 1
                ? `Mic is on. Record up to ${recordSeconds} seconds and we'll slice it into clips.`
                : 'Mic is on. Click to record.'}
            </p>
            <LiveSpectrogram analyser={analyser} context={audioContext} pixelsPerSecond={48} />
          </>
        )}
        {status === 'recording' && (
          <>
            <p className="bl-mic__overlay bl-mic__overlay--live" role="status">
              Recording… {Math.ceil(recordSeconds * (1 - recordingProgress))}s left
            </p>
            <LiveSpectrogram analyser={analyser} context={audioContext} pixelsPerSecond={48} />
            <div className="bl-mic__progress" aria-hidden="true">
              <div
                className="bl-mic__progress-fill"
                style={{ width: `${Math.min(100, recordingProgress * 100)}%` }}
              />
            </div>
          </>
        )}
        {status !== 'recording' && status !== 'error' && pending && previewUrl && (
          <div className="bl-mic__preview">
            <p className="bl-mic__preview-summary" role="status">
              Recorded {recordSeconds}s. That gives you {sliceCount}{' '}
              {sliceCount === 1 ? 'clip' : 'clips'}.
            </p>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption -- a fresh
                kid recording has no caption track to point at. */}
            <audio
              controls
              src={previewUrl}
              className="bl-mic__preview-audio"
              aria-label="Listen back to the recording"
            />
          </div>
        )}
      </div>
      <div className="bl-mic__controls">
        {pending ? (
          <>
            <button
              type="button"
              className="bl-mic__capture"
              onClick={() => void extractPending()}
              disabled={status !== 'ready' || sliceCount === 0}
            >
              {sliceCount === 0
                ? 'Extract sample'
                : `Extract ${sliceCount} ${sliceCount === 1 ? 'sample' : 'samples'}`}
            </button>
            <button
              type="button"
              className="bl-mic__close"
              onClick={discardPending}
              disabled={status !== 'ready'}
            >
              Discard
            </button>
          </>
        ) : (
          <button
            type="button"
            className={`bl-mic__capture${
              status === 'recording' ? ' bl-mic__capture--capturing' : ''
            }`}
            disabled={status !== 'ready'}
            onClick={() => void startRecording()}
          >
            {buttonLabel}
          </button>
        )}
        <button type="button" className="bl-mic__close" onClick={onClose}>
          Close mic
        </button>
      </div>
      {status === 'ready' && error && (
        <p role="alert" className="bl-mic__capture-error">
          {error}
        </p>
      )}
    </div>
  );
}
